import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import MenuModal from '../../components/Setup/MenuModal';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Menu } from '../../types/diet';
import { ColumnDef } from '@tanstack/react-table';
import DataGrid from '../../components/DataGrid';
import { useToast } from '../../components/UI/ToastContainer';

const MenusPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<Menu | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Menu | null>(null);
  const pageSize = 50;
  const [forceDeleteMenu, setForceDeleteMenu] = useState<Menu | null>(null);

  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ name?: string; status?: string }>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['menus', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => dietService.getMenus({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy,
      sortOrder,
      name: filters.name,
      status: filters.status,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteMenu(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menus'] });
      setConfirmDelete(null);
      showSuccess('Menu deleted', 'The menu has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete menu. Please try again.';
      if (/recipes/i.test(message)) {
        const current = confirmDelete;
        setConfirmDelete(null);
        if (current) setForceDeleteMenu(current);
      } else {
        showError('Delete failed', message);
      }
    },
  });

  const forceDeleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteMenu(id, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menus'] });
      setForceDeleteMenu(null);
      showSuccess('Menu deleted', 'The menu and all its recipes and schedules were deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to force delete menu. Please try again.';
      showError('Force delete failed', message);
    },
  });



  const handleSort = (column: string) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('asc'); }
  };

  const handleExport = async () => {
    try {
      const blob = await dietService.exportMenus({
        search: searchTerm,
        ids: selectedIds.length ? selectedIds : undefined,
        name: filters.name,
        status: filters.status,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `menus-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed', err); }
  };

  const openCreate = () => { setSelected(null); setIsModalOpen(true); };
  const openEdit = useCallback((m: Menu) => { setSelected(m); setIsModalOpen(true); }, []);

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkAction = async (actionKey: 'activate' | 'deactivate') => {
    try {
      if (!selectedIds.length) return;
      const status = actionKey === 'activate' ? 'ACTIVE' : 'INACTIVE';
      await dietService.bulkUpdateMenuStatus(selectedIds, status);
      showSuccess('Success', `${selectedIds.length} menu(s) ${status.toLowerCase()}`);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['menus'] });
    } catch (e: any) {
      showError('Bulk action failed', e?.message || '');
    }
  };
  const getStatusColor = (s?: string) => (s === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800');


  const clearFilters = () => { setFilters({}); setSearchTerm(''); setCurrentPage(1); };

  const columns = useMemo<ColumnDef<Menu>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      meta: { sortable: true },
      cell: (info) => info.getValue() as string,
    },
    {
      id: 'diet',
      header: 'Diet',
      cell: ({ row }) => (row.original as Menu).diet?.name || '-',
    },
    {
      id: 'recipes',
      header: 'Recipes',
      cell: ({ row }) => ((row.original as any)._count?.recipes ?? '-') as any,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor((row.original as any).status)}`}>
          {(row.original as any).status}
        </span>
      ),

    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button onClick={() => navigate(`/setup/menus/${(row.original as Menu).id}/manage`)} className="text-primary-600 hover:text-primary-900" title="Manage">
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
          <button onClick={() => openEdit(row.original as Menu)} className="text-primary-600 hover:text-primary-900" title="Edit">
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button onClick={() => setConfirmDelete(row.original as Menu)} className="text-red-600 hover:text-red-900" title="Delete">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [openEdit, setConfirmDelete, navigate]);



  if (error) {
    return (

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error loading menus.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Menus</h1>
        <div className="flex items-center space-x-3">
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('activate')}>Activate</Button>
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('deactivate')}>Deactivate</Button>
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button variant="outline" disabled={selectedIds.length !== 1} onClick={() => navigate(`/setup/menus/${selectedIds[0]}/manage`)}>Manage</Button>
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input type="text" placeholder="Search menus..." className="pl-10 form-input" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                value={filters.name || ''}
                onChange={(e) => { setFilters((f) => ({ ...f, name: e.target.value || undefined })); setCurrentPage(1); }}
                placeholder="Filter name"
                className="form-input form-input-sm"
                type="text"
              />
              <select
                className="form-input form-input-sm"
                value={filters.status ?? ''}
                onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value || undefined })); setCurrentPage(1); }}
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              <div className="flex items-center">
                <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
              </div>
            </div>
            <DataGrid
              data={data?.menus ?? []}
              columns={columns}
              getRowId={(r) => (r as Menu).id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => toggleRow(id)}
              onToggleAll={(ids, checked) =>
                setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((x) => !ids.includes(x))))
              }
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSort}
            />
            {forceDeleteMenu && (
              <DeleteConfirmModal
                title="Force Delete Menu"
                message={`This will delete "${forceDeleteMenu.name}" including all recipes and schedules. This cannot be undone. Continue?`}
                onCancel={() => setForceDeleteMenu(null)}
                onConfirm={() => forceDeleteMutation.mutate(forceDeleteMenu.id)}
                loading={forceDeleteMutation.isPending}
              />
            )}

          </>

        )}
      </div>

      {isModalOpen && (
        <MenuModal
          menu={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['menus'] }); }}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Menu"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default MenusPage;
